import { useState, useCallback, useEffect, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@pantry/ui';
import { ShoppingCartIcon, PauseIcon, PlayIcon, XIcon, CheckCircle2Icon } from 'lucide-react';
import { toast } from 'sonner';
import {
  useActiveTrip,
  useStartTrip,
  useCancelTrip,
  useTripItems,
  useScanBarcode,
  type ConnectionStatus,
  type ActiveShopper,
} from '@pantry/api-client';
import { ContinuousBarcodeScanner } from '../barcode/ContinuousBarcodeScanner';

interface TripScannerSectionProps {
  householdId: string;
  listId: string;
  /** Realtime connection status from useTripRealtimeWeb */
  connectionStatus?: ConnectionStatus;
  /** Other active shoppers from useTripPresence */
  otherShoppers?: ActiveShopper[];
  /** Total shopper count */
  shopperCount?: number;
  /** Callback when trip starts (to enable realtime) */
  onTripStart?: (tripId: string) => void;
  /** Callback when trip ends */
  onTripEnd?: () => void;
  /** Callback when user wants to complete the trip */
  onCompleteTrip?: (tripId: string) => void;
}

type ScannerState = 'idle' | 'scanning' | 'paused';

/**
 * Scanner section for active shopping trips.
 *
 * Three states:
 * - idle: no active trip → "Start Shopping" CTA
 * - scanning: camera active, showing recent scan and counter
 * - paused: trip active but camera paused
 */
export function TripScannerSection({
  householdId,
  listId,
  connectionStatus,
  otherShoppers = [],
  shopperCount = 0,
  onTripStart,
  onTripEnd,
  onCompleteTrip,
}: TripScannerSectionProps) {
  const { t } = useLingui();
  const [scannerState, setScannerState] = useState<ScannerState>('idle');
  const [lastScanResult, setLastScanResult] = useState<string | null>(null);

  const { data: activeTrip, isLoading: isLoadingTrip } = useActiveTrip(householdId, listId);
  const startTrip = useStartTrip();
  const cancelTrip = useCancelTrip();
  const scanBarcode = useScanBarcode();
  const scanBarcodeRef = useRef(scanBarcode);
  useEffect(() => {
    scanBarcodeRef.current = scanBarcode;
  });

  const tripId = activeTrip?.id ?? '';
  const { data: tripItems } = useTripItems(householdId, listId, tripId);
  const scanCount = tripItems?.length ?? 0;

  const hasActiveTrip = !!activeTrip && activeTrip.status === 'active';

  useEffect(() => {
    if (hasActiveTrip && scannerState === 'idle') {
      setScannerState('paused');
    }
  }, [hasActiveTrip, scannerState]);

  const handleStartTrip = async () => {
    try {
      const trip = await startTrip.mutateAsync({ householdId, listId });
      setScannerState('scanning');
      onTripStart?.(trip.id);
    } catch {
      toast.error(t`Failed to start trip. Please try again.`);
    }
  };

  const handleEndTrip = async () => {
    if (!tripId) return;
    try {
      await cancelTrip.mutateAsync({ householdId, listId, tripId });
      setScannerState('idle');
      setLastScanResult(null);
      onTripEnd?.();
    } catch {
      toast.error(t`Failed to end trip. Please try again.`);
    }
  };

  const handleScan = useCallback(
    async (barcode: string): Promise<boolean> => {
      if (!tripId) return false;
      try {
        const result = await scanBarcodeRef.current.mutateAsync({
          householdId,
          listId,
          tripId,
          barcode,
        });
        const productName = result.tripItem?.productName ?? barcode;
        setLastScanResult(productName);
        return true;
      } catch {
        toast.error(t`Scan failed. Try again.`);
        return false;
      }
    },
    [householdId, listId, tripId, t]
  );

  if (isLoadingTrip) return null;

  // State: No active trip → Start Shopping CTA
  if (!hasActiveTrip && scannerState === 'idle') {
    return (
      <button
        onClick={() => void handleStartTrip()}
        disabled={startTrip.isPending}
        className="w-full bg-primary text-primary-foreground rounded-lg py-4 px-6 flex items-center justify-center gap-3 hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        <ShoppingCartIcon className="h-5 w-5" />
        <div className="text-left">
          <span className="block font-medium">
            <Trans>Start Shopping</Trans>
          </span>
          <span className="block text-sm text-primary-foreground/70">
            <Trans>Scan items as you shop to check them off</Trans>
          </span>
        </div>
      </button>
    );
  }

  // State: Scanner Paused
  if (scannerState === 'paused') {
    return (
      <div className="bg-muted/50 rounded-xl p-4 transition-all duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>📷</span>
            <Trans>Scanner paused</Trans>
            {scanCount > 0 && (
              <span className="ml-2 text-xs">
                · {scanCount} {scanCount === 1 ? 'scanned' : 'scanned'}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setScannerState('scanning')}>
              <PlayIcon className="h-3.5 w-3.5 mr-1" />
              <Trans context="action_button">Resume</Trans>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => void handleEndTrip()}
            >
              <XIcon className="h-3.5 w-3.5 mr-1" />
              <Trans context="action_button">End Trip</Trans>
            </Button>
          </div>
        </div>

        {/* Presence indicator */}
        {otherShoppers.length > 0 && (
          <PresenceIndicator shoppers={otherShoppers} connectionStatus={connectionStatus} />
        )}
      </div>
    );
  }

  // State: Scanner Active
  return (
    <div className="space-y-3">
      <div className="bg-muted/50 rounded-xl p-4 transition-all duration-300">
        {/* Camera preview */}
        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-black mb-3">
          <ContinuousBarcodeScanner
            onScan={handleScan}
            onClose={() => setScannerState('paused')}
            itemCount={scanCount}
          />
        </div>

        {/* Last scan result + counter */}
        {lastScanResult && (
          <div className="flex items-center justify-between text-sm mb-3">
            <div className="flex items-center gap-1.5">
              <CheckCircle2Icon className="h-4 w-4 text-scan-success" />
              <span className="font-medium text-foreground">{lastScanResult}</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {scanCount} <Trans>scanned</Trans>
            </span>
          </div>
        )}

        {/* Controls */}
        <div className="flex justify-between">
          <Button variant="outline" size="sm" onClick={() => setScannerState('paused')}>
            <PauseIcon className="h-3.5 w-3.5 mr-1" />
            <Trans context="action_button">Pause</Trans>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => void handleEndTrip()}
          >
            <XIcon className="h-3.5 w-3.5 mr-1" />
            <Trans context="action_button">End Trip</Trans>
          </Button>
        </div>
      </div>

      {/* Presence indicator */}
      {otherShoppers.length > 0 && (
        <PresenceIndicator shoppers={otherShoppers} connectionStatus={connectionStatus} />
      )}

      {/* Connection status */}
      {connectionStatus === 'reconnecting' && (
        <div className="bg-scan-suggest/10 text-scan-suggest text-xs py-1.5 px-3 rounded-md text-center">
          <Trans>Reconnecting...</Trans>
        </div>
      )}
      {connectionStatus === 'disconnected' && (
        <div className="bg-destructive/10 text-destructive text-xs py-1.5 px-3 rounded-md text-center">
          <Trans>Connection lost. Scanning still works — changes will sync when reconnected.</Trans>
        </div>
      )}

      {/* Trip action bar — sticky bottom */}
      {hasActiveTrip && (
        <div className="sticky bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t shadow-sm p-4 z-20">
          <Button className="w-full rounded-lg py-3" onClick={() => onCompleteTrip?.(tripId)}>
            <Trans context="action_button">Complete Trip</Trans>
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-1">
            {scanCount} <Trans>scanned</Trans>
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PresenceIndicator({
  shoppers,
  connectionStatus,
}: {
  shoppers: ActiveShopper[];
  connectionStatus?: ConnectionStatus;
}) {
  if (shoppers.length === 0) return null;

  return (
    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground animate-in fade-in duration-200">
      {shoppers.map((shopper) => (
        <span key={shopper.userId} className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-presence-active inline-block" />
          {shopper.name}
        </span>
      ))}
      <span className="flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-presence-self inline-block" />
        <Trans>You</Trans>
      </span>
    </div>
  );
}
